import AppRoot from "@cleverbase-trust-drivers/shared/components/base/App";
import { useTranslation } from "react-i18next";
import Locale from "@cleverbase-trust-drivers/shared/components/i18n/Locale";
import i18next from "./i18n";
import IdentificationComponent from "./IdentificationComponent";
import getIdentificationFlow from "./IdentificationFlowClient";
import getStubbedIntentificationFlow from "./IdentificationFlowTestClient";
import { isDesktop } from "react-device-detect";
import { AuthenticationTextFields } from "@cleverbase-trust-drivers/shared/components/authentication/AuthenticationComponent";
import { ConsentTextFields } from "@cleverbase-trust-drivers/shared/components/consent/ConsentComponent";
import { IdentificationFlow } from "./domain/IdentificationState";
import { Theme } from "@cleverbase-trust-drivers/shared/styles/Themes";

function App() {
  const theme: Theme = { type: "cleverbase" };
  const { t } = useTranslation(["identification", "base"]);

  const supportedLocales: Array<Locale> = [
    { key: "en", displayValue: "English", icon: "🇬🇧" },
    { key: "nl", displayValue: "Nederlands", icon: "🇳🇱" },
  ];

  function setLanguage(lng: string): void {
    i18next.changeLanguage(lng);
  }
  const currentLanguage = i18next.language;

  const authenticationTranslations: AuthenticationTextFields = {
    authenticating: {
      manualEntry: t("base:authentication.authenticating.manualEntry"),
      qrCodeEntry: t("base:authentication.authenticating.qrCodeEntry"),
      confirmWithApp: t("base:authentication.authenticating.confirmWithApp"),
      AppLocation: t("base:authentication.authenticating.AppLocation"),
      AppLocationTextOnThisDevice: t(
        "base:authentication.authenticating.AppLocationTextOnThisDevice",
      ),
      AppLocationTextDifferentDevice: t(
        "base:authentication.authenticating.AppLocationTextDifferentDevice",
      ),
      disclaimerText: t("base:consent.requesting.disclaimerText"),
      clientContextHeaderText: t(
        "base:consent.requesting.clientContextHeaderName",
      ),
      confirmationText: t("base:consent.requesting.confirmationText"),
      onlyOpenIdScopeText: t("onlyOpenIdScopeText"),
      returnToChooseApplication: t(
        "base:authentication.authenticating.returnToChooseApplication",
      ),
      openCleverbaseAppToContinue: t(
        "base:authentication.authenticating.openCleverbaseAppToContinue",
      ),
    },
    confirming: {
      redirecting: {
        redirectingText: t("base:redirecting.redirectingText"),
        loadingText: t("base:redirecting.loadingText"),
        continueButtonText: t("base:redirecting.continueButtonText"),
      },
      nonPKIoWarning: t("base:consent.confirming.nonPKIoWarning"),
    },
    authenticated: {
      title: t("identification:title.login"),
      mainText: "",
      expandTitle: "",
      expandText: "",
    },
  };

  const consentTranslation: ConsentTextFields = {
    informing: {
      enterPinInstructions: "",
      confirmationText: t("base:consent.requesting.confirmationText"),
      disclaimerText: t("base:consent.requesting.disclaimerText"),
      clientContextHeaderText: t(
        "base:consent.requesting.clientContextHeaderName",
      ),
      nonPKIoWarning: t("base:consent.confirming.nonPKIoWarning"),
      onlyOpenIdScopeText: t("onlyOpenIdScopeText"),
    },
    confirming: {
      redirecting: {
        redirectingText: t("base:redirecting.redirectingText"),
        loadingText: t("base:redirecting.loadingText"),
        continueButtonText: t("base:redirecting.continueButtonText"),
      },
    },
  };

  function setClient(): Promise<IdentificationFlow> {
    const isDevelop = process.env.NODE_ENV === "development";
    if (isDevelop) {
      console.log(process.env.NODE_ENV);
      return getStubbedIntentificationFlow();
    } else {
      return getIdentificationFlow();
    }
  }

  return (
    <AppRoot
      header={{
        currentLanguage: currentLanguage,
        supportedLocales,
        setLanguage,
        isDesktop,
        aboutText: t("base:header.about"),
        registerText: t("base:header.register"),
        supportText: t("base:header.support"),
        aboutUrl: t("base:header.aboutUrl"),
        registerUrl: t("base:header.registerUrl"),
        supportUrl: t("base:header.supportUrl"),
      }}
      footer={{
        helpText: t("base:footer.help"),
        aboutText: t("base:footer.aboutText"),
        registerText: t("base:footer.registerText"),
        isDesktop,
        helpSteps: [
          t("base:footer.helpStep1"),
          t("base:footer.helpStep2"),
          t("base:footer.helpStep3"),
        ],
        aboutUrl: t("base:footer.aboutUrl"),
        registerUrl: t("base:footer.registerUrl"),
      }}
      isDesktop={isDesktop}
      theme={theme}
    >
      <IdentificationComponent
        client={setClient}
        rateMs={500}
        isDesktop={isDesktop}
        translations={{
          authenticationText: authenticationTranslations,
          consentText: consentTranslation,
          redirectText: {
            redirectingText: t("base:redirecting.redirectingText"),
            loadingText: t("base:redirecting.loadingText"),
            continueButtonText: "",
          },
        }}
        t={t}
      />
    </AppRoot>
  );
}

export default App;
